import { useState } from "react";
import { Btn } from "../../../components/ui/Button";
import { Chip } from "../../../components/ui/Chip";
import { approvalWithSuggestions, codeKey, codesWithSuggestedReadsAs, pluralRows } from "./rowStatus";
import type { CodeApproval, PendingCode, ResinFamily } from "../types";

interface Props {
  pendingCodes: PendingCode[];
  resinFamilies: ResinFamily[];
  canApprove: boolean;
  isBusy: boolean;
  onApprove: (approvals: CodeApproval[]) => void;
  onReject: (pendingCode: PendingCode, rejectionReason: string) => void;
}

export function PendingCodes({
  pendingCodes,
  resinFamilies,
  canApprove,
  isBusy,
  onApprove,
  onReject,
}: Props) {
  if (!pendingCodes.length) {
    return (
      <p className="m-0 text-rs-light-gray text-[13px] border border-dashed border-rs-jet rounded-md p-4 text-center">
        No codes waiting. When a file uses a code that isn't approved yet, it appears here before
        any row that uses it is saved.
      </p>
    );
  }

  const suggestedCodes = codesWithSuggestedReadsAs(pendingCodes);

  return (
    <div className="flex flex-col gap-3.5">
      {canApprove && suggestedCodes.length > 1 && (
        <Btn
          className="self-start"
          disabled={isBusy}
          onClick={() => onApprove(suggestedCodes.map(approvalWithSuggestions))}
        >
          Approve All {suggestedCodes.length} Codes with Their Reads As
        </Btn>
      )}
      {!canApprove && (
        <p className="m-0 text-rs-light-gray text-[13px]">
          Uploaders and Admins approve codes. Viewers can see what's waiting.
        </p>
      )}
      {pendingCodes.map((pendingCode) => (
        <PendingCodeCard
          key={codeKey(pendingCode)}
          pendingCode={pendingCode}
          resinFamilies={resinFamilies}
          canApprove={canApprove}
          isBusy={isBusy}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </div>
  );
}

interface CardProps {
  pendingCode: PendingCode;
  resinFamilies: ResinFamily[];
  canApprove: boolean;
  isBusy: boolean;
  onApprove: (approvals: CodeApproval[]) => void;
  onReject: (pendingCode: PendingCode, rejectionReason: string) => void;
}

function PendingCodeCard({
  pendingCode,
  resinFamilies,
  canApprove,
  isBusy,
  onApprove,
  onReject,
}: CardProps) {
  const [readsAs, setReadsAs] = useState(pendingCode.suggestedReadsAs ?? "");
  const [resinFamilyCode, setResinFamilyCode] = useState(pendingCode.suggestedResinFamilyCode ?? "");
  const [rejectionReason, setRejectionReason] = useState("");
  const idPrefix = `pending-${codeKey(pendingCode).replace(/[^A-Za-z0-9]/g, "-")}`;
  const isBaseCode = pendingCode.slotName === "Base";
  const tradenames = [...new Set(pendingCode.usedByRows.map((usedBy) => usedBy.tradename))];

  return (
    <div className="border border-rs-jet rounded-md p-3.5 flex flex-col gap-2.5">
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <code className="text-lg text-white">{pendingCode.code}</code>
        <Chip variant="info">{pendingCode.slotName} code</Chip>
        <span className="text-rs-light-gray text-xs">{pendingCode.standardCode}</span>
      </div>
      <p className="m-0 text-rs-light-gray text-xs">
        Used by {pluralRows(pendingCode.usedByRows.length)}: {tradenames.join(", ")}
      </p>

      {canApprove && (
        <>
          <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray" htmlFor={`${idPrefix}-reads-as`}>
            Reads As
            <input
              id={`${idPrefix}-reads-as`}
              className="ctl"
              value={readsAs}
              maxLength={200}
              onChange={(event) => setReadsAs(event.target.value)}
            />
          </label>
          <span className="text-rs-light-gray text-xs">
            {pendingCode.suggestedReadsAs
              ? "Taken from the Reads As column in the uploaded file."
              : "The uploaded file didn't give this code a Reads As."}
          </span>
          {isBaseCode && (
            <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray" htmlFor={`${idPrefix}-family`}>
              Resin family (optional)
              <select
                id={`${idPrefix}-family`}
                className="ctl"
                value={resinFamilyCode}
                onChange={(event) => setResinFamilyCode(event.target.value)}
              >
                <option value="">Not one of our families</option>
                {resinFamilies.map((family) => (
                  <option key={family.resinFamilyCode} value={family.resinFamilyCode}>
                    {family.displayName}
                  </option>
                ))}
              </select>
              <span>
                {pendingCode.suggestedResinFamilyCode
                  ? `Taken from the base code: ${pendingCode.code} starts with ${pendingCode.suggestedResinFamilyCode}.`
                  : "The base code doesn't start with a resin family, so none is chosen."}
              </span>
            </label>
          )}
          <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray" htmlFor={`${idPrefix}-reason`}>
            Reason, if rejecting
            <input
              id={`${idPrefix}-reason`}
              className="ctl"
              value={rejectionReason}
              maxLength={500}
              placeholder="e.g. Typo, should be PE-HD"
              onChange={(event) => setRejectionReason(event.target.value)}
            />
          </label>
          <div className="flex gap-2 flex-wrap">
            <Btn
              disabled={isBusy || !readsAs.trim()}
              onClick={() =>
                onApprove([
                  {
                    standardCode: pendingCode.standardCode,
                    slotName: pendingCode.slotName,
                    code: pendingCode.code,
                    readsAs: readsAs.trim(),
                    resinFamilyCode: isBaseCode && resinFamilyCode ? resinFamilyCode : null,
                  },
                ])
              }
            >
              Approve
            </Btn>
            <Btn
              variant="ghost"
              disabled={isBusy || !rejectionReason.trim()}
              onClick={() => onReject(pendingCode, rejectionReason.trim())}
            >
              Reject
            </Btn>
          </div>
        </>
      )}
    </div>
  );
}
